import { Fragment, useEffect, useMemo, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import includes from "lodash-es/includes"
import last from "lodash-es/last"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type NavigateFunction, useNavigate } from "react-router-dom"
import * as yup from "yup"

import useBoolean from "$/hooks/use-boolean"
import { isNullOrUndefined } from "$/utils/gates"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { PRODUCT_TYPES_WITH_ENROLLMENTS } from "@/3514/config"
import { EPath } from "@/3514/constants"
import { type TUserState, useUser } from "@/3514/hooks"
import { type TLegacyCoachingSessionQuery, useLegacyCoachingSessionsQuery } from "@/3514/queries"
import { EProductTypeInternalName, EUserRole, IEnrollmentModel } from "@/3514/types"
import { buildCssClassName } from "@/3514/utils"
import useSubmitOffboarding from "@/api/mutations/use-submit-offboarding"
import useOffBoarding from "@/api/use-offboarding"
import { CoachRatingAskedFrom, CoachRatingSource } from "@/constants"
import AssessmentResponses from "@/elements/assessment-responses/AssessmentResponses"
import CoachRating from "@/elements/coach-rating/CoachRating"
import NpsQuestion from "@/elements/nps/NpsQuestion"
import { type TGetValueFromTranslatedObjectFunction, useGetValueFromTranslatedObject } from "@/hooks"
import Coach from "@/models/coach"
import LabelDropdown from "@/shared/dropdown/LabelDropdown"
import { isLikertComponent, isLikertDefaultComponent } from "@/utils/gates/utilts"

import { WarningMessage } from "../../shared/WarningMessage"

import DiagnosticQuestionsList from "./DiagnostiQuestionsList"
import OffboardingContainer from "./OffboardingContainer"

import "./styles.scss"

/**
 * @deprecated uses legacy stuff, can't and shouldn't be supported; rewrite A$AP
 */
const Offboarding = () => {
    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const hasCoachSessions = user.productType !== EProductTypeInternalName.SpotCoaching

    const { data: offboardingJourney, isLoading } = useOffBoarding()
    const submitOffboarding = useSubmitOffboarding()

    const { data: sessions }: TLegacyCoachingSessionQuery = useLegacyCoachingSessionsQuery({
        enabled: hasCoachSessions,
        participantId: user.id
    })

    const { value: showDropdown, toggle: onToggleDropdown } = useBoolean(false)
    const { value: isBusy, setValue: setIsBusy } = useBoolean(false)
    const [feedbackSubmitted, setFeedbackSubmitted] = useState({
        nps: false,
        postModuleRating: false,
        offboardingRating: false
    })
    const [error, setError] = useState(null)

    useEffect((): void => {
        if (user.isOffboardingPassed) navigate(EPath.Participant_Dashboard())
    }, [user.isOffboardingPassed, navigate])

    const module: IEnrollmentModel = last(user.enrollments)

    const selfAssessmentQuestions =
        offboardingJourney?.self_assessment?.question_list?.length > 0
            ? offboardingJourney?.self_assessment?.question_list
            : []

    const questionsList = useMemo(
        () =>
            offboardingJourney
                ? [
                      ...offboardingJourney?.module_reflection,
                      ...offboardingJourney?.journey_final_reflection,
                      ...selfAssessmentQuestions
                  ]
                : [],
        [offboardingJourney, selfAssessmentQuestions]
    )

    const hasEnrolledModules = includes<EProductTypeInternalName>(PRODUCT_TYPES_WITH_ENROLLMENTS, user?.productType)

    const coachSession = useMemo(() => {
        const hasLiveSession = !isNullOrUndefined(sessions?.live_session)

        if (!hasEnrolledModules) {
            return hasLiveSession
                ? sessions?.live_session
                : sessions?.passed_sessions?.[sessions?.passed_sessions?.length - 1]
        }

        const passedSession =
            sessions?.passed_sessions?.find(session => session.enrollment_rank === module?.rank) ??
            sessions?.passed_sessions?.[0]

        const session = hasLiveSession
            ? sessions?.live_session?.enrollment_rank === module?.rank
                ? sessions?.live_session
                : passedSession
            : passedSession

        return session
    }, [sessions, module, hasEnrolledModules])

    const coach = useMemo(() => {
        return !isNullOrUndefined(coachSession?.coach) ? new Coach(coachSession.coach as never) : null
    }, [coachSession])

    const activeDiagnosticAnswers = useMemo(
        () => offboardingJourney?.self_assessment?.answer_data?.filter(answer => answer.is_active),
        [offboardingJourney]
    )

    const shapeWithDynamicValidation = useMemo(() => {
        const validationSchema = Object.create({})
        if (questionsList.length) {
            validationSchema.diagnostic = yup.object(
                questionsList.reduce((validationMap, question) => {
                    validationMap[question.uuid] = question.response_required
                        ? yup
                              .string()
                              .max(5046, t("Ensure this field has no more than 5046 characters"))
                              .required(t("Answer is required"))
                              .nullable(true)
                        : yup.string().notRequired().nullable(true)
                    return validationMap
                }, {})
            )
        }
        return validationSchema
    }, [questionsList])

    const methods = useForm({
        resolver: yupResolver(yup.object(shapeWithDynamicValidation))
    })

    const {
        handleSubmit,
        setError: setFormError,
        clearErrors,
        formState: { errors }
    } = methods

    const onSubmit = values => {
        const answersList = getAnswersList(values)
        if (!feedbackSubmitted.nps) {
            setFormError("nps", { type: "custom", message: t("Answer is required") })
            return
        }

        submitData({
            question_list: questionsList,
            answer_list: answersList
        })
    }

    const getAnswersList = values => {
        return questionsList.map(question => {
            const answer = values.diagnostic[question.uuid]
            let answerObj: any = {
                question_uuid: question.uuid,
                question: question.question,
                answer: answer ?? ""
            }

            if (isLikertComponent(question.component_type) || isLikertDefaultComponent(question)) {
                const option = question.options.find(option => option.uuid === answer)
                answerObj = {
                    ...question,
                    question_uuid: question.uuid,
                    question: question.question,
                    option_uuid: answer,
                    answer: option?.response?.[user.languageCode] ?? answer ?? ""
                }
            }

            if (isNullOrUndefined(answerObj.option_uuid)) {
                delete answerObj.option_uuid
            }

            return answerObj
        })
    }

    const submitData = async data => {
        setIsBusy(true)
        try {
            await submitOffboarding.mutateAsync(data)
            navigate(EPath.Participant_Dashboard())
        } catch (e) {
            setError(getErrorMessages(e))
        } finally {
            setIsBusy(false)
        }
    }

    const onSubmitNps = () => {
        setFeedbackSubmitted({
            ...feedbackSubmitted,
            nps: true
        })
        clearErrors("nps")
    }

    const onSubmitCoachRating = (source: CoachRatingSource) => {
        if (source === CoachRatingAskedFrom.POST_MODULE_REFLECTION_POPUP) {
            setFeedbackSubmitted(feedback => ({ ...feedback, postModuleRating: true }))
        }

        if (source === CoachRatingAskedFrom.OFFBOARDING) {
            setFeedbackSubmitted(feedback => ({ ...feedback, offboardingRating: true }))
        }
    }

    const hasDiagnosticQuestion = offboardingJourney?.journey_final_reflection?.length > 0

    return (
        <OffboardingContainer layoutClassName="pb-[40px] md:pb-[100px]">
            <p className="color-gray fs-14 text-center mb-2">
                {t(
                    "We're a small business looking to grow & improve, so your honest feedback in this final reflection means a lot!"
                )}
            </p>
            <div className="questions-container">
                <FormProvider {...methods}>
                    <div className={buildCssClassName("reflection-container", { "d-none": !hasCoachSessions })}>
                        <Typography
                            text={
                                <Fragment>
                                    {getValueFromTranslatedObject(module?.name)} {t("Reflection")}
                                </Fragment>
                            }
                            className="mb-5"
                            alignment={ETypographyAlignment.Left}
                            color={ETypographyColor.Black}
                            family={ETypographyFamily.DmSerif}
                            weight={ETypographyWeight.Medium}
                            size={ETypographySize.Heading2}
                            tag={ETypographyTag.Heading2}
                        />
                        {hasCoachSessions && (
                            <>
                                <Typography
                                    text={t("My Experience")}
                                    className="mb-4"
                                    alignment={ETypographyAlignment.Left}
                                    color={ETypographyColor.Black}
                                    family={ETypographyFamily.DmSerif}
                                    weight={ETypographyWeight.Medium}
                                    size={ETypographySize.Medium}
                                    tag={ETypographyTag.Heading3}
                                />
                                <CoachRating
                                    participantId={user.id}
                                    askedFrom={CoachRatingAskedFrom.POST_MODULE_REFLECTION_POPUP}
                                    coachSessionId={coachSession?.session_id}
                                    coachData={coach}
                                    onSubmitCoachRating={onSubmitCoachRating}
                                />
                            </>
                        )}
                        {offboardingJourney?.module_reflection?.length > 0 &&
                        offboardingJourney?.self_assessment?.question_list?.length > 0 ? (
                            <Typography
                                text={t("My Development")}
                                className="mb-4"
                                alignment={ETypographyAlignment.Left}
                                color={ETypographyColor.Black}
                                family={ETypographyFamily.DmSerif}
                                weight={ETypographyWeight.Medium}
                                size={ETypographySize.Medium}
                                tag={ETypographyTag.Heading3}
                            />
                        ) : null}
                        {activeDiagnosticAnswers?.length ? (
                            <LabelDropdown
                                text={`${showDropdown ? t("Hide") : t("Show")} ${t("My Previous Responses")}`}
                                active={showDropdown}
                                onToggle={onToggleDropdown}
                                className="offboarding mb-4 font-extrabold"
                            />
                        ) : null}

                        {showDropdown && (
                            <AssessmentResponses
                                diagnosticAnswers={activeDiagnosticAnswers}
                                title={t("Initial Assessment Responses")}
                                className="mb-[40px]"
                                variant="offboarding"
                            />
                        )}

                        <DiagnosticQuestionsList
                            diagnosticQuestions={offboardingJourney?.module_reflection}
                            name="diagnostic"
                            errors={errors}
                            isLoading={isLoading}
                        />

                        <DiagnosticQuestionsList
                            diagnosticQuestions={offboardingJourney?.self_assessment?.question_list}
                            name="diagnostic"
                            errors={errors}
                            isLoading={isLoading}
                        />
                    </div>

                    <div className="reflection-container">
                        <Typography
                            text={t("Journey Reflection")}
                            className="mb-5"
                            alignment={ETypographyAlignment.Left}
                            color={ETypographyColor.Black}
                            family={ETypographyFamily.DmSerif}
                            weight={ETypographyWeight.Medium}
                            size={ETypographySize.Heading2}
                            tag={ETypographyTag.Heading2}
                        />
                        {hasCoachSessions && (
                            <>
                                <Typography
                                    text={t("My Experience")}
                                    className="mb-4"
                                    alignment={ETypographyAlignment.Left}
                                    color={ETypographyColor.Black}
                                    family={ETypographyFamily.DmSerif}
                                    weight={ETypographyWeight.Medium}
                                    size={ETypographySize.Medium}
                                    tag={ETypographyTag.Heading3}
                                />
                                <CoachRating
                                    participantId={user.id}
                                    askedFrom={CoachRatingAskedFrom.OFFBOARDING}
                                    onSubmitCoachRating={onSubmitCoachRating}
                                    orderNumber={1}
                                />
                            </>
                        )}

                        <NpsQuestion
                            onSubmitNps={onSubmitNps}
                            errorMsg={errors?.nps?.message}
                            showDividerLine={hasDiagnosticQuestion}
                            order={hasCoachSessions ? 2 : null}
                        />

                        {hasDiagnosticQuestion && (
                            <Typography
                                text={t("My Development")}
                                className="mb-4"
                                alignment={ETypographyAlignment.Left}
                                color={ETypographyColor.Black}
                                family={ETypographyFamily.DmSerif}
                                weight={ETypographyWeight.Medium}
                                size={ETypographySize.Medium}
                                tag={ETypographyTag.Heading3}
                            />
                        )}

                        <DiagnosticQuestionsList
                            diagnosticQuestions={offboardingJourney?.journey_final_reflection}
                            errors={errors}
                            name="diagnostic"
                            isLoading={isLoading}
                        />
                    </div>

                    <WarningMessage message={error} />
                    <div className="mt-[50px]">
                        <Button isCentered onClick={handleSubmit(onSubmit)} isBusy={isBusy}>
                            {t("common.submit")}
                        </Button>
                    </div>
                </FormProvider>
            </div>
        </OffboardingContainer>
    )
}

export default Offboarding
